import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getMyLostItemCases, lostItemStatusBadge } from '../../services/disputeService';
import toast from 'react-hot-toast';

export default function MyLostItemCases() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    if (!userProfile?.id) return;
    getMyLostItemCases(userProfile.id, 'driver')
      .then(setCases)
      .catch((err) => toast.error(err.message || 'Could not load cases'))
      .finally(() => setLoading(false));
  }, [userProfile?.id]);

  const TERMINAL = ['resolved_returned', 'resolved_collected', 'unresolved',
    'expired_driver_custody', 'unresolved_pickup_timeout', 'cancelled_by_rider', 'admin_resolved'];
  const ACTION_REQUIRED = ['reported', 'awaiting_driver_fee_confirmation', 'return_paid', 'return_in_progress'];

  const filtered = cases.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'active') return !TERMINAL.includes(c.status);
    if (filter === 'action_required') return ACTION_REQUIRED.includes(c.status);
    if (filter === 'closed') return TERMINAL.includes(c.status);
    return true;
  });

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const actionRequiredCount = cases.filter(c => ACTION_REQUIRED.includes(c.status)).length;

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/driver')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Dashboard</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Lost items</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Cases involving items lost or found in your vehicle.
      </p>

      <button onClick={() => navigate('/driver/report-found-item')} className="w-full p-3 rounded-xl cursor-pointer font-semibold mb-4"
        style={{ border: '1.5px dashed #2dd4a8', background: '#f0fdfa', color: '#0a8270' }}>+ Proactively report a found item</button>

      {/* Filter tabs */}
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {[
          { key: 'active', label: 'Active' },
          { key: 'action_required', label: `Action needed${actionRequiredCount > 0 ? ` (${actionRequiredCount})` : ''}` },
          { key: 'closed', label: 'Closed' },
          { key: 'all', label: 'All' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} className="px-3 py-1.5 rounded-full cursor-pointer font-semibold text-[0.78rem]"
            style={{
              border: `1.5px solid ${filter === f.key ? '#2dd4a8' : '#e2e8f0'}`,
              background: filter === f.key ? 'rgba(45,212,168,0.06)' : '#fff',
              color: filter === f.key ? '#2dd4a8' : '#5a6578',
            }}>{f.label}</button>
        ))}
      </div>

      {loading && <div className="p-10 text-center text-muted">Loading...</div>}

      {!loading && filtered.length === 0 && (
        <div className="p-10 text-center text-muted bg-white rounded-2xl border border-slate-200">
          {filter === 'all' ? 'No lost item cases.' : `No ${filter.replace('_', ' ')} cases.`}
        </div>
      )}

      <div className="grid gap-3">
        {filtered.map(c => {
          const badge = lostItemStatusBadge(c.status);
          const needsAction = ACTION_REQUIRED.includes(c.status);
          return (
            <div key={c.id} onClick={() => navigate(`/driver/lost-items/${c.id}`)} className="p-4 rounded-2xl bg-white cursor-pointer transition-all"
              style={{ border: `${needsAction ? '2px' : '1px'} solid ${needsAction ? '#2dd4a8' : '#e2e8f0'}` }}>
              <div className="flex justify-between items-start mb-1.5">
                <div className="font-bold text-brand-dark">{c.referenceNumber}</div>
                <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
                  {badge.label}
                </span>
              </div>
              <div className="text-[0.85rem] text-body mb-1">
                {c.description?.substring(0, 100)}{c.description?.length > 100 ? '...' : ''}
              </div>
              <div className="flex justify-between text-[0.78rem] text-muted">
                <span>Rider: {c.riderName || '—'}</span>
                <span>{fmtDate(c.createdAt)}</span>
              </div>
              {needsAction && (
                <div className="mt-2 p-1.5 rounded-md text-[0.78rem] font-semibold" style={{ background: 'rgba(45,212,168,0.1)', color: '#0a8270' }}>
                  ⚡ Action needed
                </div>
              )}
            </div>
          );
        })}
      </div>
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}